/**
 * src/context/CMSContext.tsx
 *
 * Fetches the full CMS payload from WordPress once on mount,
 * stores it in context, and provides a typed accessor hook.
 * Falls back to hardcoded defaults when the API is unreachable (dev / offline).
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { cmsApi, type CMSData } from '../lib/api';

// ── Context ────────────────────────────────────────────────────────────────────
interface CMSContextValue {
  cms: CMSData;
  loading: boolean;
  error: string | null;
  /** Drill into nested CMS value with dot-path, e.g. "home.hero.headline" */
  get: <T = string>(path: string, fallback?: T) => T;
}

const CMSContext = createContext<CMSContextValue>({
  cms: {},
  loading: true,
  error: null,
  get: (_path, fallback) => (fallback as unknown) as never,
});

// ── Provider ───────────────────────────────────────────────────────────────────
export function CMSProvider({ children }: { children: ReactNode }) {
  const [cms, setCms] = useState<CMSData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    cmsApi
      .getAll()
      .then((data) => {
        if (!cancelled) setCms(data);
      })
      .catch((err) => {
        if (!cancelled) setError(String(err?.message ?? 'CMS fetch failed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Drill into nested object by dot-path, e.g. "home.hero.headline" */
  function get<T = string>(path: string, fallback?: T): T {
    const parts = path.split('.');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let cur: any = cms;
    for (const part of parts) {
      if (cur == null || typeof cur !== 'object') return (fallback ?? '') as T;
      cur = cur[part];
    }
    return (cur ?? fallback ?? '') as T;
  }

  return (
    <CMSContext.Provider value={{ cms, loading, error, get }}>
      {children}
    </CMSContext.Provider>
  );
}

// ── Hook ───────────────────────────────────────────────────────────────────────
export function useCMS() {
  return useContext(CMSContext);
}

/**
 * Convenience hook for a single page's CMS data.
 * Returns an object of section values for that page.
 */
export function useCMSPage(page: string) {
  const { cms, loading, error } = useContext(CMSContext);
  return { data: cms[page] ?? {}, loading, error };
}
